import { ENEMY, TILE } from '../config/config';
import type { World } from '../game/world';
import { C } from '../gfx/palette';
import type { Renderer } from '../gfx/renderer';
import { Enemy } from './enemy';

type Phase = 'hidden' | 'rise' | 'out' | 'retract';

const ART_H = 24;
const OUT_H = 22;

/**
 * Nipper — a snapping bloom that lives in a pipe. It emerges on a fixed cycle, but never
 * while the player is standing right on top of the pipe, so it can always be avoided.
 * It cannot be stomped: jump over it, or hit it with an energy orb.
 */
export class Nipper extends Enemy {
  private phase: Phase = 'hidden';
  private timer = 0;
  private anim = 0;
  /** Top of the pipe lip this nipper lives in. */
  private pipeTop: number;
  /** Horizontal centre (fixed). */
  private cx: number;
  /** How far the head currently sticks out of the pipe. */
  private protrude = 0;

  /**
   * `x`,`y` come from the spawn marker placed one tile *above* the pipe lip, so the lip
   * itself is one tile lower.
   */
  constructor(x: number, y: number) {
    super(x + 9, y, 14, 0);
    this.pipeTop = y + TILE;
    this.cx = x + TILE;
    this.x = this.cx - 7;
    this.y = this.pipeTop;
    this.timer = ENEMY.nipperHidden;
    this.turnAtEdge = false;
  }

  protected override get particleColor(): string {
    return C.nipper;
  }

  override update(dt: number, world: World): void {
    if (this.updateTumble(dt, world)) return;
    this.anim += dt;
    this.timer -= dt;

    switch (this.phase) {
      case 'hidden': {
        const nearPlayer = Math.abs(world.playerCenterX - this.cx) < ENEMY.nipperSafeRange;
        if (this.timer <= 0 && !nearPlayer) {
          this.phase = 'rise';
          this.timer = ENEMY.nipperRise;
        } else if (this.timer <= 0) {
          this.timer = 0.12; // keep re-checking; do not surface under the player
        }
        this.protrude = 0;
        break;
      }
      case 'rise':
        this.protrude = OUT_H * (1 - Math.max(0, this.timer) / ENEMY.nipperRise);
        if (this.timer <= 0) {
          this.phase = 'out';
          this.timer = ENEMY.nipperOut;
          this.protrude = OUT_H;
        }
        break;
      case 'out':
        this.protrude = OUT_H;
        if (this.timer <= 0) {
          this.phase = 'retract';
          this.timer = ENEMY.nipperRise;
        }
        break;
      case 'retract':
        this.protrude = OUT_H * (Math.max(0, this.timer) / ENEMY.nipperRise);
        if (this.timer <= 0) {
          this.phase = 'hidden';
          this.timer = ENEMY.nipperHidden;
          this.protrude = 0;
        }
        break;
    }

    // The hitbox only exists while the head is actually outside the pipe.
    this.h = this.protrude > 3 ? this.protrude : 0;
    this.y = this.pipeTop - this.protrude;
  }

  /** Nippers cannot be stomped — landing on one hurts. */
  override onStomp(): boolean {
    return false;
  }

  override onOrb(world: World): boolean {
    this.defeatFlip(world, 200, 0);
    return true;
  }

  override onShellHit(world: World, dir: number): void {
    this.defeatFlip(world, world.nextShellScore(), dir);
  }

  override onPrism(world: World): void {
    this.defeatFlip(world, 200, 0);
  }

  override draw(r: Renderer): void {
    if (this.tumbling) {
      this.drawTumbling(r, 'nipper.0');
      return;
    }
    if (this.protrude <= 0) return;
    const frame = Math.floor(this.anim * 5) % 2;
    const buf = r.sprites.get(`nipper.${frame}`);
    const top = this.pipeTop - this.protrude;
    // Clip everything that is still inside the pipe.
    const srcY = 0;
    const srcH = Math.min(ART_H, Math.max(0, this.protrude));
    r.blitClipped(buf, this.cx - buf.w / 2, top, srcY, srcH);
  }
}
